import React, {useCallback, useEffect, useState, useRef} from 'react';
import {View, StyleSheet, ScrollView, FlatList, Dimensions} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Button,
  Chip,
  Divider,
  Snackbar,
  RadioButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute, CommonActions} from '@react-navigation/native';
import WebView from 'react-native-webview';
import {useSubscriptionStore} from '../../store/useSubscriptionStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const {width: SCREEN_WIDTH} = Dimensions.get('window');
const CARD_WIDTH = SCREEN_WIDTH * 0.78;
const CARD_MARGIN = spacing.sm;
const GST_RATE = 0.18;

const BulkRenewalScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {selectedStores} = route.params || {};

  const {plans, isLoading, fetchPlans, bulkInitiatePayment, verifyPayment} =
    useSubscriptionStore();
  const {fetchOrgUnits} = useOrgUnitStore();

  const [selectedFamilyIndex, setSelectedFamilyIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [snackbar, setSnackbar] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [txnId, setTxnId] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const flatListRef = useRef(null);

  const paidPlans = plans.filter(p => p.planType === 'PAID');
  const storeCount = selectedStores?.length || 0;

  useEffect(() => {
    fetchPlans().catch(e =>
      setSnackbar(e.response?.data?.message || t('common.error')),
    );
  }, [fetchPlans, t]);

  useEffect(() => {
    if (paidPlans.length > 0 && paidPlans[selectedFamilyIndex]) {
      const variants = paidPlans[selectedFamilyIndex].variants || [];
      if (variants.length > 0 && !selectedVariantId) {
        setSelectedVariantId(variants[0].id);
      }
    }
  }, [paidPlans, selectedFamilyIndex, selectedVariantId]);

  const getSelectedVariant = useCallback(() => {
    if (!selectedVariantId || paidPlans.length === 0) return null;
    const family = paidPlans[selectedFamilyIndex];
    return family?.variants?.find(v => v.id === selectedVariantId);
  }, [selectedVariantId, selectedFamilyIndex, paidPlans]);

  const computeTotals = useCallback(() => {
    const variant = getSelectedVariant();
    if (!variant) return {perStore: 0, discount: 0, afterDiscount: 0, tax: 0, total: 0};

    const listPrice = parseFloat(variant.listPrice) || 0;
    const discountPct = parseFloat(variant.discountPct) || 0;
    const perStoreDiscount = listPrice * (discountPct / 100);
    const perStoreAfterDiscount = listPrice - perStoreDiscount;
    const perStoreTax = perStoreAfterDiscount * GST_RATE;
    const perStoreTotal = perStoreAfterDiscount + perStoreTax;

    return {
      perStore: listPrice,
      discount: perStoreDiscount * storeCount,
      afterDiscount: perStoreAfterDiscount * storeCount,
      tax: perStoreTax * storeCount,
      total: perStoreTotal * storeCount,
      subtotal: listPrice * storeCount,
    };
  }, [getSelectedVariant, storeCount]);

  const handleFamilyScroll = useCallback(
    event => {
      const offsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / (CARD_WIDTH + CARD_MARGIN * 2));
      if (index !== selectedFamilyIndex && index >= 0 && index < paidPlans.length) {
        setSelectedFamilyIndex(index);
        const variants = paidPlans[index]?.variants || [];
        if (variants.length > 0) {
          setSelectedVariantId(variants[0].id);
        }
      }
    },
    [selectedFamilyIndex, paidPlans],
  );

  const handlePay = useCallback(async () => {
    if (!selectedVariantId) {
      setSnackbar(t('subscription.selectVariant'));
      return;
    }
    setProcessing(true);
    try {
      const items = selectedStores.map(store => ({
        orgUnitId: store.id,
        orgUnitName: store.name,
        subscriptionId: store.subscriptionId || null,
      }));
      const result = await bulkInitiatePayment(selectedVariantId, items);
      setTxnId(result.txnId);
      setPaymentUrl(result.paymentUrl);
    } catch (e) {
      setSnackbar(
        e.response?.data?.message || e.response?.data?.error?.message || t('common.error'),
      );
      setProcessing(false);
    }
  }, [selectedVariantId, selectedStores, bulkInitiatePayment, t]);

  const handlePaymentComplete = useCallback(
    async success => {
      setPaymentUrl(null);
      if (!success) {
        setProcessing(false);
        setSnackbar(t('subscription.paymentFailed'));
        return;
      }
      setVerifying(true);
      try {
        await verifyPayment(txnId);
        await fetchOrgUnits();
        navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{name: 'MyAccountMain'}],
          }),
        );
      } catch (e) {
        setSnackbar(
          e.response?.data?.message ||
            e.response?.data?.error?.message ||
            t('common.error'),
        );
      } finally {
        setProcessing(false);
        setVerifying(false);
      }
    },
    [txnId, verifyPayment, fetchOrgUnits, navigation, t],
  );

  // WebView payment
  if (paymentUrl) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction
            onPress={() => {
              setPaymentUrl(null);
              setProcessing(false);
            }}
            color={colors.white}
          />
          <Appbar.Content
            title={t('subscription.payment')}
            titleStyle={styles.appbarTitle}
          />
        </Appbar.Header>
        <WebView
          source={{uri: paymentUrl}}
          style={{flex: 1}}
          onNavigationStateChange={navState => {
            if (navState.url?.includes('tailorapp.com/payment/callback')) {
              handlePaymentComplete(true);
            }
          }}
          onShouldStartLoadWithRequest={request => {
            if (request.url.includes('tailorapp.com/payment/callback')) {
              handlePaymentComplete(true);
              return false;
            }
            return true;
          }}
          javaScriptEnabled
          domStorageEnabled
          startInLoadingState
          renderLoading={() => (
            <View style={styles.centerContent}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.loadingText}>
                {t('subscription.loadingPayment')}
              </Text>
            </View>
          )}
          onError={() => handlePaymentComplete(false)}
        />
      </View>
    );
  }

  if (verifying) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.verifyingText}>
          {t('subscription.verifyingPayment')}
        </Text>
      </View>
    );
  }

  const totals = computeTotals();
  const variant = getSelectedVariant();

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
          disabled={processing}
        />
        <Appbar.Content
          title={t('myAccount.bulkRenewal')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={{flex: 1}} showsVerticalScrollIndicator={false}>
        {/* Selected stores summary */}
        <View style={styles.storesSummary}>
          <View style={styles.storesSummaryHeader}>
            <MaterialCommunityIcons
              name="store-check"
              size={20}
              color={colors.primary}
            />
            <Text style={styles.storesSummaryTitle}>
              {t('myAccount.storesSelected', {count: storeCount})}
            </Text>
          </View>
          {selectedStores?.map(store => (
            <View key={store.id} style={styles.storeChipRow}>
              <Text style={styles.storeChipName} numberOfLines={1}>
                {store.name}
              </Text>
              <Chip
                style={styles.expiredChip}
                textStyle={styles.expiredChipText}
                compact>
                <Text style={[styles.expiredChipText,{marginTop: 2, fontSize:10}]}>{store.subscriptionStatus || 'EXPIRED'}</Text>
              </Chip>
            </View>
          ))}
          <Text style={styles.samePlanNote}>
            {t('myAccount.samePlanForAll')}
          </Text>
        </View>

        {/* Plan selection carousel */}
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <>
            <View style={styles.dotsRow}>
              {paidPlans.map((_, i) => (
                <View
                  key={i}
                  style={[
                    styles.dot,
                    i === selectedFamilyIndex && styles.dotActive,
                  ]}
                />
              ))}
            </View>

            <FlatList
              ref={flatListRef}
              data={paidPlans}
              renderItem={({item: fam, index}) => {
                const variants = fam.variants || [];
                return (
                  <View
                    style={[
                      styles.planCard,
                      index === selectedFamilyIndex && styles.planCardSelected,
                    ]}>
                    <View style={styles.planHeader}>
                      <Text style={styles.planName}>{fam.name}</Text>
                    </View>
                    <View style={styles.variantsContainer}>
                      <Text style={styles.variantsTitle}>
                        {t('subscription.chooseDuration')}
                      </Text>
                      {variants.map(v => {
                        const hasDiscount =
                          v.discountPct && parseFloat(v.discountPct) > 0;
                        return (
                          <View
                            key={v.id}
                            style={[
                              styles.variantRow,
                              selectedVariantId === v.id &&
                                styles.variantRowSelected,
                            ]}>
                            <RadioButton.Android
                              value={String(v.id)}
                              status={
                                selectedVariantId === v.id
                                  ? 'checked'
                                  : 'unchecked'
                              }
                              onPress={() => setSelectedVariantId(v.id)}
                              color={colors.primary}
                            />
                            <View style={styles.variantInfo}>
                              <Text style={styles.variantName}>{v.name}</Text>
                              <View style={styles.variantPriceRow}>
                                {hasDiscount && (
                                  <Text style={styles.listPrice}>
                                    {'\u20B9'}
                                    {v.listPrice}
                                  </Text>
                                )}
                                <Text style={styles.netPrice}>
                                  {'\u20B9'}
                                  {v.netPrice}
                                </Text>
                                <Text style={styles.perStoreLabel}>
                                  {t('myAccount.perStore')}
                                </Text>
                                {hasDiscount && (
                                  <Chip
                                    style={styles.discountChip}
                                    textStyle={styles.discountChipText}
                                    compact>
                                    <Text style={[styles.discountChipText,{marginTop: 2, fontSize:10}]}>{v.discountPct}% off</Text>
                                  </Chip>
                                )}
                              </View>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                );
              }}
              keyExtractor={item => String(item.id)}
              horizontal
              pagingEnabled={false}
              snapToInterval={CARD_WIDTH + CARD_MARGIN * 2}
              snapToAlignment="start"
              decelerationRate="fast"
              showsHorizontalScrollIndicator={false}
              onMomentumScrollEnd={handleFamilyScroll}
              contentContainerStyle={styles.carouselContent}
            />
          </>
        )}

        {/* Consolidated invoice preview */}
        {variant && (
          <View style={styles.invoicePreview}>
            <Text style={styles.sectionTitle}>
              {t('subscription.orderSummary')}
            </Text>

            {/* Line items */}
            {selectedStores?.map(store => (
              <View key={store.id} style={styles.lineItemRow}>
                <Text style={styles.lineItemName} numberOfLines={1}>
                  {store.name}
                </Text>
                <Text style={styles.lineItemPrice}>
                  {'\u20B9'}
                  {parseFloat(variant.listPrice).toFixed(2)}
                </Text>
              </View>
            ))}

            <Divider style={{marginVertical: spacing.sm}} />

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>{t('subscription.subtotal')}</Text>
              <Text style={styles.totalValue}>
                {'\u20B9'}
                {totals.subtotal.toFixed(2)}
              </Text>
            </View>

            {totals.discount > 0 && (
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, styles.discountText]}>
                  {t('subscription.discount')}
                </Text>
                <Text style={[styles.totalValue, styles.discountText]}>
                  -{'\u20B9'}
                  {totals.discount.toFixed(2)}
                </Text>
              </View>
            )}

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                {t('subscription.gst')} (18%)
              </Text>
              <Text style={styles.totalValue}>
                {'\u20B9'}
                {totals.tax.toFixed(2)}
              </Text>
            </View>

            <Divider style={{marginVertical: spacing.sm}} />

            <View style={styles.totalRow}>
              <Text style={styles.grandTotalLabel}>
                {t('myAccount.totalForStores', {count: storeCount})}
              </Text>
              <Text style={styles.grandTotalValue}>
                {'\u20B9'}
                {totals.total.toFixed(2)}
              </Text>
            </View>
          </View>
        )}

        <View style={{height: 100}} />
      </ScrollView>

      {/* Pay button */}
      {variant && (
        <View style={styles.bottomBar}>
          <Button
            mode="contained"
            onPress={handlePay}
            loading={processing}
            disabled={processing || !selectedVariantId}
            buttonColor={colors.primary}
            contentStyle={styles.payButtonContent}
            style={styles.payButton}
            icon="lock">
            {processing
              ? t('subscription.processing')
              : `${t('subscription.payNow')} \u20B9${totals.total.toFixed(2)}`}
          </Button>
        </View>
      )}

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  loadingContainer: {padding: spacing.xxl, alignItems: 'center'},
  loadingText: {marginTop: spacing.md, fontSize: 14, color: colors.textSecondary},
  verifyingText: {marginTop: spacing.md, fontSize: 16, color: colors.text, fontWeight: '600'},

  // Stores summary
  storesSummary: {
    backgroundColor: colors.surface,
    margin: spacing.md,
    borderRadius: 12,
    padding: spacing.md,
  },
  storesSummaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: spacing.sm,
  },
  storesSummaryTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  storeChipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  storeChipName: {
    fontSize: 14,
    color: colors.text,
    flex: 1,
    marginRight: spacing.sm,
  },
  expiredChip: {
    backgroundColor: colors.error + '18',
    height: 24,
  },
  expiredChipText: {
    fontSize: 11,
    color: colors.error,
    fontWeight: '600',
  },
  samePlanNote: {
    fontSize: 12,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginTop: spacing.sm,
    textAlign: 'center',
  },

  // Plan carousel
  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    gap: 6,
  },
  dot: {width: 8, height: 8, borderRadius: 4, backgroundColor: colors.border},
  dotActive: {width: 20, backgroundColor: colors.primary},
  carouselContent: {paddingHorizontal: spacing.md, paddingBottom: spacing.md},
  planCard: {
    width: CARD_WIDTH,
    marginHorizontal: CARD_MARGIN,
    backgroundColor: colors.surface,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  planCardSelected: {
    borderColor: colors.primary,
    elevation: 4,
  },
  planHeader: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
  },
  planName: {fontSize: 20, fontWeight: '700', color: colors.white},
  variantsContainer: {padding: spacing.md},
  variantsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  variantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 4,
  },
  variantRowSelected: {backgroundColor: colors.primary + '0D'},
  variantInfo: {flex: 1},
  variantName: {fontSize: 14, fontWeight: '500', color: colors.text},
  variantPriceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  listPrice: {
    fontSize: 13,
    color: colors.textLight,
    textDecorationLine: 'line-through',
  },
  netPrice: {fontSize: 15, fontWeight: '700', color: colors.primary},
  perStoreLabel: {fontSize: 12, color: colors.textSecondary},
  discountChip: {backgroundColor: colors.success + '18', height: 25},
  discountChipText: {fontSize: 11, color: colors.success},

  // Invoice preview
  invoicePreview: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.md,
    borderRadius: 12,
    padding: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  lineItemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  lineItemName: {fontSize: 14, color: colors.text, flex: 1, marginRight: spacing.sm},
  lineItemPrice: {fontSize: 14, color: colors.text, fontWeight: '500'},
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
  },
  totalLabel: {fontSize: 14, color: colors.textSecondary},
  totalValue: {fontSize: 14, color: colors.text},
  discountText: {color: colors.success, fontWeight: '500'},
  grandTotalLabel: {fontSize: 16, fontWeight: '700', color: colors.text},
  grandTotalValue: {fontSize: 18, fontWeight: '700', color: colors.primary},

  // Bottom bar
  bottomBar: {
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  payButton: {borderRadius: 8},
  payButtonContent: {paddingVertical: spacing.xs},
});

export default BulkRenewalScreen;
